<template>
  <div 
    v-show="isVisible"
    class="fixed top-12 lg:top-16 left-0 right-0 z-30 bg-white border-b border-gray-200 transform transition-transform duration-300 ease-out"
    :class="isVisible ? 'translate-y-0' : '-translate-y-full'"
  >
    <div class="max-w-screen-2xl mx-auto px-4 lg:px-8 py-0">
      <div class="flex items-center justify-between">
        <!-- Collection Navigation Tabs -->
        <div class="flex items-center space-x-1 font-serif">
        <span class="text-xl font-medium mr-2 text-dark-teal-600">{{ t('collectionNavigation.title') }}</span> 
          <button
            v-for="collection in collections"
            :key="collection.id"
            @click="navigateToCollection(collection.id)"
            class="px-4 py-2 text-xl font-medium transition-all duration-200"
            :class="[
              currentCollection === collection.id
                ? 'bg-dark-teal-600 border-b-2 text-white hover:border-b-light-brown-500'
                : 'text-gray-400 hover:text-dark-teal-600 black border-b-2 border-b-transparent hover:border-b-dark-teal-600'
            ]"
          >
            {{ collection.name }}
          </button>
        </div>

        <!-- Collection Status -->
        <div v-if="currentCollectionStatus.text !== 'Unknown' || currentCollectionStatus.text !== 'Unknown'" class="flex items-center space-x-4">
          <div class="flex items-center space-x-2"
          :class="currentCollection === 1 ? 'hidden md:block' : ''"
          >
            <span class="text-sm text-gray-600">{{ t('collectionNavigation.status.label') }}</span>
            <span 
              class="px-2 py-1 text-xs font-semibold rounded-full"
              :class="currentCollectionStatus.class"
            >
              {{ currentCollectionStatus.text }}
            </span>
          </div>
          
          <!-- Countdown for current (active) season -->
          <div v-if="currentCollection === currentSeasonIndex" class="flex items-center space-x-2">
            <span class="relative flex size-2">
              <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
              <span class="relative inline-flex size-2 rounded-full bg-red-500"></span>
            </span>
            <span class="text-sm font-mono text-gray-600">{{ formattedCountdown || '—' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from '#imports'
import { useCountdown, getNextSeasonLaunchCET } from '~/composables/useCountdown'
import { useSeasons } from '~/composables/useSeasons'
import { collectionsData } from '~/data/collections'
import type { CollectionStatus } from '~/data/collections'

const props = defineProps({
  currentCollection: {
    type: Number,
    default: 1
  }
})

const emit = defineEmits(['navigate'])
const { t, locale } = useI18n()
const { currentSeasonIndex, getSeasonStatus, getSeasonPeriod } = useSeasons()

const collections = computed(() =>
  collectionsData.map((collection) => ({
    id: collection.id,
    name: collection.name,
    status: getSeasonStatus(collection.id as 1 | 2 | 3 | 4),
    period: getSeasonPeriod(collection.id as 1 | 2 | 3 | 4, undefined, locale?.value ?? 'en'),
  }))
)

const isVisible = ref(false)
const observer = ref<IntersectionObserver | null>(null)

const nextLaunch = ref<Date | null>(null)
onMounted(() => {
  nextLaunch.value = getNextSeasonLaunchCET()
})
const { formattedCountdown } = useCountdown(nextLaunch)

type StatusKey = CollectionStatus | 'unknown'

const statusClassMap: Record<StatusKey, string> = {
  active: 'bg-green-100 text-green-800',
  upcoming: 'bg-blue-100 text-blue-800',
  past: 'bg-gray-100 text-gray-600',
  unknown: 'bg-gray-100 text-gray-600'
}

const currentCollectionStatus = computed(() => {
  const collection = collections.value.find((c) => c.id === props.currentCollection)
  const statusKey = (collection?.status ?? 'unknown') as StatusKey
  return {
    text: t(`collectionNavigation.status.${statusKey}`),
    class: statusClassMap[statusKey]
  }
})

const navigateToCollection = (collectionId: number) => {
  if (collectionId === props.currentCollection) return
  
  emit('navigate', collectionId)
  window.location.href = `/collection/${collectionId}`
}

const setupIntersectionObserver = () => {
  const collectionSection = document.querySelector('[data-collection-section]')
  if (!collectionSection) return

  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isVisible.value = entry.isIntersecting
        
        if (entry.isIntersecting) {
          document.body.classList.add('collection-nav-visible')
        } else {
          document.body.classList.remove('collection-nav-visible')
        }
      })
    },
    {
      threshold: 0.1,
      rootMargin: '-100px 0px 0px 0px'
    }
  )

  observer.value.observe(collectionSection)
}

onMounted(() => {
  setTimeout(() => {
    setupIntersectionObserver()
  }, 100)
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
  document.body.classList.remove('collection-nav-visible')
})
</script>

<style scoped>
/* Add padding to body when collection navigation is visible */
:global(body.collection-nav-visible) {
  padding-top: 4rem; /* 64px - height of collection navigation */
}

/* Add spacing to collection section when navigation is visible */
:global(.collection-nav-spacer) {
  transition: padding-top 0.3s ease-out;
}

:global(body.collection-nav-visible .collection-nav-spacer) {
  padding-top: 6rem; /* 96px - extra space for collection navigation */
}
</style>
